import { experienceUrl } from '@/lib/urls';
import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Star, Crown, Gift, Ticket, Calendar, Sparkles, 
  CreditCard, Settings, ChevronRight, CheckCircle,
  Clock, Percent, Users, Heart, Search, Filter
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/contexts/AuthContext';
import ConsumerLayout from '@/components/ConsumerLayout';
import api from '@/lib/api';
import { toast } from 'sonner';

const GoldMemberDashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [goldContent, setGoldContent] = useState({ events: [], experiences: [] });
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('benefits');
  
  // Check if this is a new member (from payment success)
  const isNewMember = searchParams.get('welcome') === 'true';

  useEffect(() => {
    if (authLoading) return;
    
    if (!isAuthenticated) {
      navigate('/auth');
      return;
    }
    
    if (!user?.is_gold_member) {
      navigate('/gold-club');
      return;
    }
    
    fetchGoldContent();
  }, [isAuthenticated, user, authLoading]);

  const fetchGoldContent = async () => {
    try {
      // Fetch Gold-exclusive and Gold-priced content
      const [eventsRes, experiencesRes] = await Promise.all([
        api.get('/events?gold_only=true'),
        api.get('/vouchers?gold_only=true')
      ]);
      
      setGoldContent({
        events: eventsRes.data || [],
        experiences: experiencesRes.data || []
      });
    } catch (error) {
      console.error('Failed to fetch gold content:', error);
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    { icon: Percent, title: 'Exclusive Discounts', description: 'Up to 25% off on experiences and events', color: 'text-amber-500' },
    { icon: Ticket, title: 'Priority Access', description: 'First dibs on sold-out events and limited experiences', color: 'text-indigo-500' },
    { icon: Crown, title: 'Members-Only Events', description: 'Access exclusive Gold-only happenings', color: 'text-purple-500' },
    { icon: Sparkles, title: 'VIP Concierge', description: 'Personalized recommendations from Evis AI', color: 'text-rose-500' },
    { icon: Gift, title: 'Birthday Perks', description: 'Special offers during your birthday month', color: 'text-emerald-500' },
    { icon: Users, title: 'Guest Passes', description: 'Bring friends at member rates (2x/month)', color: 'text-blue-500' },
  ];

  const filteredEvents = goldContent.events.filter(e => 
    e.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    e.venue_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredExperiences = goldContent.experiences.filter(e => 
    e.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    e.venue_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (authLoading || loading) {
    return (
      <ConsumerLayout darkTheme hideFooter>
        <div className="flex items-center justify-center py-40">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500" />
        </div>
      </ConsumerLayout>
    );
  }

  return (
    <ConsumerLayout darkTheme>
      
      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Welcome Banner for New Members */}
        {isNewMember && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 p-6 bg-gradient-to-r from-amber-500/20 via-yellow-500/20 to-amber-500/20 rounded-2xl border border-amber-500/30"
          >
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center">
                <Star className="w-8 h-8 text-amber-900" fill="currentColor" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">Welcome to Gold Club, {user?.full_name?.split(' ')[0] || 'Member'}!</h2>
                <p className="text-amber-200">Your exclusive membership is now active. Explore your benefits below.</p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Member Card */}
        <div className="grid lg:grid-cols-3 gap-8 mb-8">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:col-span-1"
          >
            <Card className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 border-2 border-amber-500/50 overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-amber-500/20 to-transparent rounded-full blur-2xl" />
              <CardContent className="p-6 relative">
                <div className="flex items-center gap-2 mb-4">
                  <Star className="w-6 h-6 text-amber-400" fill="currentColor" />
                  <span className="text-amber-400 text-sm font-semibold tracking-wider uppercase">Gold Member</span>
                </div>
                
                <h3 className="text-2xl font-bold text-white mb-1">{user?.full_name || 'Gold Member'}</h3>
                <p className="text-slate-400 text-sm mb-6">{user?.email}</p>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-400">Member Since</span>
                    <span className="text-white font-medium">
                      {new Date().toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-400">Status</span>
                    <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Active</Badge>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-400">Renewal</span>
                    <span className="text-white font-medium">
                      {new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                  </div>
                </div>
                
                <div className="mt-6 pt-4 border-t border-slate-700">
                  <Button 
                    variant="outline" 
                    className="w-full border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                    onClick={() => navigate('/dashboard')}
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Manage Subscription
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Quick Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="lg:col-span-2 grid sm:grid-cols-3 gap-4"
          >
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-amber-500/20 flex items-center justify-center">
                  <Percent className="w-6 h-6 text-amber-400" />
                </div>
                <p className="text-3xl font-bold text-white">$0</p>
                <p className="text-sm text-slate-400">Total Saved</p>
              </CardContent>
            </Card>
            
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-indigo-500/20 flex items-center justify-center">
                  <Ticket className="w-6 h-6 text-indigo-400" />
                </div>
                <p className="text-3xl font-bold text-white">{goldContent.events.length}</p>
                <p className="text-sm text-slate-400">Gold Events</p>
              </CardContent>
            </Card>
            
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-purple-500/20 flex items-center justify-center">
                  <Gift className="w-6 h-6 text-purple-400" />
                </div>
                <p className="text-3xl font-bold text-white">{goldContent.experiences.length}</p>
                <p className="text-sm text-slate-400">Gold Experiences</p>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-slate-800 border border-slate-700">
            <TabsTrigger value="benefits" className="data-[state=active]:bg-amber-500 data-[state=active]:text-amber-900">
              <Crown className="w-4 h-4 mr-2" />
              Benefits
            </TabsTrigger>
            <TabsTrigger value="events" className="data-[state=active]:bg-amber-500 data-[state=active]:text-amber-900">
              <Ticket className="w-4 h-4 mr-2" />
              Gold Events
            </TabsTrigger>
            <TabsTrigger value="experiences" className="data-[state=active]:bg-amber-500 data-[state=active]:text-amber-900">
              <Gift className="w-4 h-4 mr-2" />
              Gold Experiences
            </TabsTrigger>
          </TabsList>

          {/* Benefits Tab */}
          <TabsContent value="benefits">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={benefit.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="bg-slate-800/50 border-slate-700 h-full hover:border-amber-500/50 transition-colors">
                    <CardContent className="p-6">
                      <div className={`w-12 h-12 rounded-xl bg-slate-700/50 flex items-center justify-center mb-4 ${benefit.color}`}>
                        <benefit.icon className="w-6 h-6" />
                      </div>
                      <h3 className="text-lg font-semibold text-white mb-2">{benefit.title}</h3>
                      <p className="text-slate-400 text-sm">{benefit.description}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </TabsContent>

          {/* Gold Events Tab */}
          <TabsContent value="events">
            <div className="mb-6">
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  placeholder="Search Gold events..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-slate-800 border-slate-700 text-white"
                />
              </div>
            </div>
            
            {filteredEvents.length === 0 ? (
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-12 text-center">
                  <Ticket className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">No Gold Events Yet</h3>
                  <p className="text-slate-400 mb-6">Check back soon for exclusive member events!</p>
                  <Button onClick={() => navigate('/events')} className="bg-amber-500 hover:bg-amber-600 text-amber-900">
                    Browse All Events
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredEvents.map((event) => (
                  <Card 
                    key={event.id} 
                    className="bg-slate-800/50 border-slate-700 overflow-hidden cursor-pointer hover:border-amber-500/50 transition-colors"
                    onClick={() => navigate(`/events/${event.id}`)}
                  >
                    <div className="aspect-video bg-slate-700 relative">
                      {event.images?.[0] && (
                        <img src={event.images[0]} alt={event.title} className="w-full h-full object-cover" />
                      )}
                      <Badge className="absolute top-3 right-3 bg-amber-500 text-amber-900">
                        <Star className="w-3 h-3 mr-1" fill="currentColor" />
                        Gold
                      </Badge>
                    </div>
                    <CardContent className="p-4">
                      <h3 className="font-semibold text-white mb-1 line-clamp-1">{event.title}</h3>
                      <p className="text-sm text-slate-400 mb-3">{event.venue_name}</p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-sm text-slate-400">
                          <Calendar className="w-4 h-4" />
                          {event.date}
                        </div>
                        {event.gold_price && (
                          <div className="text-right">
                            <span className="text-slate-500 line-through text-sm">${event.price}</span>
                            <span className="text-amber-400 font-bold ml-2">${event.gold_price}</span>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Gold Experiences Tab */}
          <TabsContent value="experiences">
            <div className="mb-6">
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  placeholder="Search Gold experiences..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-slate-800 border-slate-700 text-white"
                />
              </div>
            </div>
            
            {filteredExperiences.length === 0 ? (
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-12 text-center">
                  <Gift className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">No Gold Experiences Yet</h3>
                  <p className="text-slate-400 mb-6">Exclusive experiences are coming soon!</p>
                  <Button onClick={() => navigate('/marketplace')} className="bg-amber-500 hover:bg-amber-600 text-amber-900">
                    Browse All Experiences
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredExperiences.map((exp) => (
                  <Card 
                    key={exp.id} 
                    className="bg-slate-800/50 border-slate-700 overflow-hidden cursor-pointer hover:border-amber-500/50 transition-colors"
                    onClick={() => navigate(experienceUrl(exp))}
                  >
                    <div className="aspect-video bg-slate-700 relative">
                      {exp.images?.[0] && (
                        <img src={exp.images[0]} alt={exp.title} className="w-full h-full object-cover" />
                      )}
                      <Badge className="absolute top-3 right-3 bg-amber-500 text-amber-900">
                        <Star className="w-3 h-3 mr-1" fill="currentColor" />
                        Gold Price
                      </Badge>
                    </div>
                    <CardContent className="p-4">
                      <h3 className="font-semibold text-white mb-1 line-clamp-1">{exp.title}</h3>
                      <p className="text-sm text-slate-400 mb-3">{exp.venue_name}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-slate-500 line-through text-sm">${exp.price}</span>
                        <span className="text-amber-400 font-bold text-lg">${exp.gold_price}</span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
      
    </ConsumerLayout>
  );
};

export default GoldMemberDashboard;
